import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { useWeb3React } from '@web3-react/core';
import { Web3Provider } from '@ethersproject/providers';
import { ethers, Contract } from 'ethers';
import { motion } from 'framer-motion';
import DJackGameDiceRoll from '../components/DJackGame/DJackGameDiceRoll';
import DJackGameTotalPoints from '../components/DJackGame/DJackGameTotalPoints';
import DJackGameLeaveButton from '../components/DJackGame/DJackGameLeaveButton';
import DJackGameTotalPrize from '../components/DJackGame/DJackGameTotalPrize';
import DJackGameDiceStats from '../components/DJackGame/DJackGameDiceStats';
import DJackGameEventsLog from '../components/DJackGame/DJackGameEventsLog';
import DJackInfoCard from '../components/DJackInfo/DJackInfoCard';
import DJackTitle from '../components/Graphics/DJackTitle';
import DJackGameStandings from '../components/DJackGame/DJackGameStandings';
import managerABI from '../contracts/djackGame/ABI';
import DJackGameCountdown from '../components/DJackGame/DJackGameCountdown';

interface IDjackEventLog {
  time: number;
  text: string;
}

const DJackGame = () => {
  // contract definition
  const location = useLocation();
  const path = location.pathname.split('/')[2];

  const { library } = useWeb3React<Web3Provider>();

  const provider = new ethers.providers.StaticJsonRpcProvider(
    'https://data-seed-prebsc-1-s1.binance.org:8545/'
  );

  const managerAddress = process.env.REACT_APP_DJACKGAME_MANAGER_ADDRESS;
  const contract = new Contract(
    `${managerAddress}`,
    managerABI.ABI,
    library?.getSigner() ? library?.getSigner() : provider
  );

  // events log
  const [djackEventsLog, setDjackEventsLog] = useState<IDjackEventLog[]>([]);

  // states and contexts
  const [entryFee, setEntryFee] = useState<string>(
    ethers.BigNumber.from('0').toString()
  ); // static
  const [players, setPlayers] = useState<string[]>(
    new Array(5).fill(ethers.constants.AddressZero)
  );
  const [scores, setScores] = useState<number[]>([0, 0, 0, 0, 0]);
  const [totalPrize, setTotalPrize] = useState<string>(
    ethers.BigNumber.from('0').toString()
  );
  const [gameState, setGameState] = useState<number>(0); // game states 0: not created, 1: not started, 2: started, 3: ended
  const [isClaimable, setIsClaimable] = useState<boolean>(false);
  const [isResetable, setIsResetable] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true); // for button
  const [remainingTime, setRemainingTime] = useState<number>(180);

  // fetching game info
  const getPeriodicGameInfo = async () => {
    try {
      const gameInfo = await contract.getGameInfo(path);
      const fetchedEntryFee = gameInfo[5].toString();
      setEntryFee(fetchedEntryFee);
      const fetchedPlayers = gameInfo[1];
      setPlayers(fetchedPlayers);
      let fetchedscores = gameInfo[2];
      fetchedscores = fetchedscores.map((point: any) => point.toNumber());
      setScores(fetchedscores);
      const fetchedTotalPrize = gameInfo[4];
      setTotalPrize(fetchedTotalPrize.toString());
      const fetchedGameState = gameInfo[3];
      setGameState(fetchedGameState);
      if (fetchedGameState === 2) {
        const fetchedIsClaimable = await contract.isClaimable(path);
        setIsClaimable(fetchedIsClaimable);
        const fetchedIsResetable = await contract.isResetable(path);
        setIsResetable(fetchedIsResetable);
      } else {
        setIsClaimable(false);
        setIsResetable(false);
      }
      const fetchedStartTime = await contract.getStartTime(path);
      if (fetchedStartTime.toNumber() !== 0) {
        const remainingSeconds = Math.floor(
          180 - (Date.now() / 1000 - fetchedStartTime.toNumber())
        );
        if (remainingSeconds < 0 && remainingSeconds > -60) {
          setRemainingTime(remainingSeconds + 60);
        } else if (remainingSeconds <= -60) {
          setRemainingTime(0);
        } else {
          setRemainingTime(remainingSeconds);
        }
      }
      setIsLoading(false);
    } catch (error) {
      console.error(error);
    }
  };

  // cleanup states on unmount
  const cleanup = () => {
    setEntryFee(ethers.BigNumber.from('0').toString());
    setPlayers(new Array(5).fill(ethers.constants.AddressZero));
    setScores([0, 0, 0, 0, 0]);
    setTotalPrize(ethers.BigNumber.from('0').toString());
    setGameState(0);
    setIsClaimable(false);
    setIsLoading(true);
    setRemainingTime(180);
  };

  // lifecycle hooks
  useEffect(() => {
    provider.on('block', () => {
      getPeriodicGameInfo();
    });
    return () => {
      provider.off('block', () => {
        getPeriodicGameInfo();
      });
      cleanup();
    };
  }, []);

  return (
    <motion.div
      key="djackGame"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="card">
        <div className="cardTitle">
          <DJackTitle />
        </div>
        <div className="innerCard">
          <div className="innerCardTitle innerCardTitleWithButton">
            <span>Gaming Arena</span>
            <DJackGameLeaveButton players={players} contract={contract} />
          </div>
          <div className="djackGameGamingAreaWrapper">
            <div className="djackGameCountdownWrapper">
              <DJackGameCountdown
                remainingTime={remainingTime}
                gameState={gameState}
              />
            </div>
            <DJackGameTotalPoints
              displayIndex={1}
              players={players}
              totalPoints={scores}
            />
            <DJackGameDiceRoll
              gameState={gameState}
              players={players}
              isClaimable={isClaimable}
              isResetable={isResetable}
              contract={contract}
              isLoading={isLoading}
              scores={scores}
              entryFee={entryFee}
            />
            <DJackGameTotalPoints
              displayIndex={2}
              players={players}
              totalPoints={scores}
            />
          </div>
        </div>
        <div className="innerCard">
          <div className="innerCardTitle">
            <span>Standings</span>
          </div>
          <div className="djackGameStandingsWrapper">
            <DJackGameStandings
              players={players}
              totalPoints={scores}
              entryFee={entryFee}
              contract={contract}
            />
          </div>
        </div>
        <div className="innerCard">
          <DJackGameTotalPrize totalPrize={totalPrize} />
        </div>
        <div className="innerCard">
          <div className="djackGameStatsWrapper">
            <DJackGameDiceStats entryFee={entryFee} />
          </div>
        </div>
        <div className="innerCard" style={{ height: '200px' }}>
          <div className="innerCardTitle">Events Log</div>
          <DJackGameEventsLog
            djackEventsLog={djackEventsLog}
            setDjackEventsLog={setDjackEventsLog}
          />
        </div>
      </div>
      <DJackInfoCard />
    </motion.div>
  );
};
export default DJackGame;
